import { useMutation, useQuery } from 'react-query'
import { toast } from 'react-toastify'
import queryClient from '~/data/queryClient'
import { getInspectionChecklistById } from '~/modules/inspection_checklist/data/inspectionChecklistApi'
import {
  IDiscrepancy,
  IInspection,
  IInspectionInvitation,
  IOperatorFeedback,
  ISubmissionValues,
} from '~/utils/types'
import {
  IAircraftPhotoResponse,
  IDeleteInspectionInvitationProps,
  IDownloadOperatorFeedbackEvidenceParams,
  IDownloadRecordFileByInspectionId,
  IGetReportPhotoCategoryImage,
  IRevokeInspectionInvitationProps,
  IgetProgressInspectionMigrateDataParams,
  checkOutdatedChecklist,
  createOperatorFeedback,
  deleteInspectionInvitation,
  deleteOperatorFeedbackEvidence,
  downloadInspectionFile,
  downloadOperatorFeedbackEvidence,
  downloadZipFile,
  getAircraftImageUrl,
  getDiscrepancyWithPhotos,
  getInspectionById,
  getInspectionCheckStatusToClose,
  getInspectionReportFiles,
  getNotificationPreferences,
  getOperatorFeedbackByDiscrepancyId,
  getOperatorFeedbackById,
  getOperatorFeedbackFiles,
  getPreviousInspection,
  getProgressInspectionMigrateData,
  getReportExports,
  getReportExportsById,
  getReportPhotoCategoryImage,
  inviteUserToInspection,
  listInspectionInvitations,
  removeInspectionFile,
  removeOperatorFeedbackFile,
  revokeInspectionInvitation,
  updateOperatorFeedback,
  uploadInspectionFiles,
  uploadOperatorFeedback,
  uploadOperatorFeedbackEvidences,
} from './api'
import { diffSubmissionValues } from '../utils/diffSubmissionValues'
import {
  IDashboardInspectionInformation,
  IReviewSubmission,
  IUpdateInspectionRequest,
  createInspection,
  getAircraftHistoryByAircraftId,
  getDashboardInspectionInformation,
  reviewSubmission,
  searchAdditionalCompany,
  updateInspection,
  updateReportSubmission,
} from './inspectionApi'

export interface IUpdateSubmission {
  reportId?: string | null
  inspectionId?: string | null
  lockDateOfAudit?: boolean
  submissionValues?: ISubmissionValues | null
  /**
   * The form's pristine/last-synced-with-server submission values (e.g.
   * Formik's `initialValues.submission_values`). When provided, only the
   * diff between this and `submissionValues` is sent to the server instead
   * of the entire tree - see `diffSubmissionValues` and EYES-2764. Omit to
   * fall back to sending `submissionValues` as-is.
   */
  initialSubmissionValues?: ISubmissionValues | null
  disableToast?: boolean
}

export const useUpdateSubmission = (props: IUpdateSubmission) => {
  return useMutation({
    mutationFn: () => {
      const payload = props.initialSubmissionValues
        ? diffSubmissionValues(props.initialSubmissionValues, props.submissionValues)
        : props.submissionValues

      return updateReportSubmission({
        submissionValues: payload as ISubmissionValues | null | undefined,
        reportId: props.reportId,
        lockDateOfAudit: props.lockDateOfAudit,
      })
    },
    onSuccess: () => {
      if (props.disableToast !== true) toast.success('Report saved!')
      queryClient.invalidateQueries(['get_inspection_by_id', props.inspectionId])
      queryClient.invalidateQueries(['dashboard_inspection', props.inspectionId])
    },
  })
}

export const useGetInspectionById = (inspectionId?: string) => {
  return useQuery({
    queryKey: ['get_inspection_by_id', inspectionId],
    queryFn: () => getInspectionById(inspectionId || ''),
    staleTime: Infinity,
    enabled: Boolean(inspectionId),
  })
}

export const useNotificationPreferences = (inspectionId?: string, enabled?: boolean) => {
  return useQuery({
    queryKey: ['find_notification_preference', inspectionId],
    queryFn: () => getNotificationPreferences(inspectionId ?? ''),
    enabled: enabled,
    staleTime: 0,
  })
}

export const useGetChecklistRefByID = (inspection_checklist_id = '', enabled = true) => {
  return useQuery({
    queryKey: ['get_checklist_ref_by_id', inspection_checklist_id],
    queryFn: () => getInspectionChecklistById({ inspection_checklist_id }),
    staleTime: Infinity,
    enabled: enabled,
  })
}

export const useCheckOutdatedChecklist = (inspection_id = '', enabled = true) => {
  return useQuery({
    queryKey: ['check_outdated_checklist', inspection_id],
    queryFn: () => checkOutdatedChecklist(inspection_id),
    staleTime: Infinity,
    enabled,
  })
}

export const useGetAircraftHistoryByAircraftId = (aircraftId?: string) => {
  return useQuery({
    queryKey: ['aircraft_history_by_aircraft_id', aircraftId],
    queryFn: () =>
      getAircraftHistoryByAircraftId({
        aircraftId: aircraftId || '',
      }),
    staleTime: Infinity,
    enabled: Boolean(aircraftId),
  })
}

export const useGetAircraftImageById = (aircraftId?: string) => {
  return useQuery<IAircraftPhotoResponse>({
    queryKey: ['aircraft_image_by_id', aircraftId],
    queryFn: async () => {
      if (!aircraftId) {
        return {
          file_url: null,
        }
      }

      return getAircraftImageUrl(aircraftId)
    },
    staleTime: 15 * 60 * 1000,
  })
}
export const useGetReportExportsById = (reportId?: string) => {
  if (!reportId)
    return {
      data: null,
      isLoading: false,
    }

  return useQuery({
    queryKey: ['report_exports_by_report_id', reportId],
    queryFn: () => getReportExportsById(reportId),
    staleTime: 5000,
  })
}

export const useCreateInspection = (onSuccess?: (newInspection: IInspection) => void) => {
  return useMutation({
    mutationFn: (props: IInspection) => createInspection(props),
    onSuccess: (data) => {
      queryClient.invalidateQueries(['list_inspections'])

      if (onSuccess) {
        onSuccess(data)
      }
    },
  })
}

export const useUpdateInspection = (inspectionId: string) => {
  return useMutation({
    mutationFn: (props: IUpdateInspectionRequest) =>
      updateInspection({ ...props, inspection_id: inspectionId }),
    onSuccess: () => {
      queryClient.invalidateQueries(['get_inspection_by_id', inspectionId])
    },
  })
}

export const useReviewSubmission = (inspectionId: string) => {
  return useMutation({
    mutationFn: (props: IReviewSubmission) => reviewSubmission(props),
    onSuccess: () => {
      queryClient.invalidateQueries(['get_inspection_by_id', inspectionId])
    },
  })
}

export const useGetInspectionReportFiles = (inspectionId?: string) => {
  return useQuery({
    queryKey: ['get_inspection_report_files', inspectionId],
    queryFn: () => getInspectionReportFiles(inspectionId),
    staleTime: Infinity,
  })
}

export const useGetReportExports = (reportId: string) => {
  return useQuery({
    queryKey: ['get_report_exports', reportId],
    queryFn: () => getReportExports(reportId),
    staleTime: 5 * 60 * 1000,
  })
}

export const useGetOperatorFeedbackFiles = (findingID?: string) => {
  return useQuery({
    queryKey: ['get_operator_feedback_files', findingID],
    queryFn: () => getOperatorFeedbackFiles(findingID),
    staleTime: Infinity,
  })
}

export const useUploadInspectionFiles = (inspectionId: string) => {
  return useMutation({
    mutationFn: (files: Blob[]) => uploadInspectionFiles({ inspectionId, files }),
    onSuccess: () => {
      queryClient.invalidateQueries(['get_inspection_report_files', inspectionId])
    },
  })
}

export const useUploadOperatorFeedback = (findindId: string) => {
  return useMutation({
    mutationFn: (files: Blob[]) => uploadOperatorFeedback({ findindId, files }),
    onSuccess: () => {
      queryClient.invalidateQueries(['get_operator_feedback_files', findindId])
    },
  })
}

export const useRemoveInspectionFile = (inspectionId: string) => {
  return useMutation({
    mutationFn: (fileName: string) => removeInspectionFile({ inspectionId, fileName }),
    onSuccess: () => {
      queryClient.invalidateQueries(['get_inspection_report_files', inspectionId])
    },
  })
}

export const useRemoveOperatorFeedbackFile = (findingId: string) => {
  return useMutation({
    mutationFn: (fileName: string) => removeOperatorFeedbackFile({ findingId, fileName }),
    onSuccess: () => {
      queryClient.invalidateQueries(['get_operator_feedback_files', findingId])
    },
  })
}

export const useDownloadInspectionFile = (inspectionId: string) => {
  return useMutation({
    mutationFn: (fileName: string) => downloadInspectionFile({ inspectionId, fileName }),
  })
}

export const useDownloadRecordFileByInspectionId = () => {
  return useMutation({
    mutationFn: ({ inspectionId, fileName }: IDownloadRecordFileByInspectionId) =>
      downloadInspectionFile({ inspectionId, fileName }),
  })
}

export const useDownloadZipFile = (reportId: string, dataType: string) => {
  return useMutation({
    mutationFn: () => downloadZipFile({ reportId, dataType }),
  })
}

export const useDownloadOperatorFeedbackEvidence = () => {
  return useMutation({
    mutationFn: (params: IDownloadOperatorFeedbackEvidenceParams) =>
      downloadOperatorFeedbackEvidence(params),
  })
}

export const useUploadOperatorFeedbackEvidences = () => {
  return useMutation({
    mutationFn: uploadOperatorFeedbackEvidences,
    onSuccess(data) {
      queryClient.setQueryData(['get_operator_feedback_by_id', data._id], () => data)
    },
  })
}

type IUseGetReportPhotoCategoryImage = IGetReportPhotoCategoryImage & {
  enabled?: boolean
}

export const useGetReportPhotoCategoryImage = (props: IUseGetReportPhotoCategoryImage) => {
  return useQuery<string>({
    queryKey: ['report_photo_category_image', props.report_id, props.category_uuid],
    queryFn: () => getReportPhotoCategoryImage(props),
    enabled: props.enabled,
  })
}

interface IUseGetOperatorFeedbackById {
  operatorFeedbackId: string
  enabled?: boolean
}

interface IUseGetOperatorFeedbackByDiscrepancyId {
  discrepancyId: string
  enabled?: boolean
}

export const useGetOperatorFeedbackById = (props: IUseGetOperatorFeedbackById) => {
  return useQuery<IOperatorFeedback>({
    queryKey: ['get_operator_feedback_by_id', props.operatorFeedbackId],
    queryFn: () => getOperatorFeedbackById(props.operatorFeedbackId),
    enabled: false,
    staleTime: 15 * 60 * 1000,
  })
}

export const useGetOperatorFeedbackByDiscrepancyId = (
  props: IUseGetOperatorFeedbackByDiscrepancyId,
) => {
  return useQuery<IOperatorFeedback[]>({
    queryKey: ['get_operator_feedback_by_discrepancy_id', props.discrepancyId],
    queryFn: () => getOperatorFeedbackByDiscrepancyId(props.discrepancyId),
  })
}

export const useCreateOperatorFeedback = () => {
  return useMutation({
    mutationFn: createOperatorFeedback,
    onSuccess(data, variables) {
      queryClient.setQueriesData(['report_discrepancies'], (oldData: unknown) => {
        if (!oldData || !(oldData instanceof Array) || !data._id) return oldData

        const oldDiscrepancies = [...(oldData as IDiscrepancy[])]

        const foundIndex = oldDiscrepancies.findIndex((i) => i._id === variables.discrepancy_id)
        if (foundIndex < 0) return oldData

        oldDiscrepancies[foundIndex].operator_feedback_id = data._id
        return oldDiscrepancies
      })
      queryClient.setQueryData(['get_operator_feedback_by_id', data._id], () => data)
    },
  })
}

export const useUpdateOperatorFeedback = () => {
  return useMutation({
    mutationFn: updateOperatorFeedback,
    onSuccess(data) {
      queryClient.setQueryData(['get_operator_feedback_by_id', data._id], () => data)
    },
  })
}

export const useDeleteOperatorFeedbackEvidence = () => {
  return useMutation({
    mutationFn: deleteOperatorFeedbackEvidence,
    onSuccess(data) {
      queryClient.setQueryData(['get_operator_feedback_by_id', data._id], () => data)
    },
  })
}

export const useListInspectionInvitations = (inspectionId: string) => {
  return useQuery({
    queryKey: ['list_inspection_invitations', inspectionId],
    queryFn: () => listInspectionInvitations(inspectionId),
    staleTime: 5 * 1000,
  })
}

export const useInviteUserToInspection = () => {
  return useMutation({
    mutationFn: inviteUserToInspection,
    onSuccess(data, props) {
      queryClient.setQueryData(
        ['list_inspection_invitations', props.inspection_id],
        (oldData: unknown) => {
          if (!oldData) return [data]

          const invitations = [...(oldData as IInspectionInvitation[])]

          invitations.push(data)
          return invitations
        },
      )
    },
  })
}

export const useRevokeInspectionInvitation = () => {
  return useMutation({
    mutationFn: revokeInspectionInvitation,
    onSuccess(data, variables) {
      const props = variables as IRevokeInspectionInvitationProps
      queryClient.setQueryData(
        ['list_inspection_invitations', props.inspection_id],
        (oldData: unknown) => {
          if (!oldData) return [data]

          const oldInvitations = [...(oldData as IInspectionInvitation[])]

          const foundIndex = oldInvitations.findIndex((i) => i._id === props.invitation_id)

          if (foundIndex < 0) return oldInvitations

          oldInvitations[foundIndex] = data

          return oldInvitations
        },
      )
    },
  })
}

export const useDeleteInspectionInvitation = () => {
  return useMutation({
    mutationFn: deleteInspectionInvitation,
    onSuccess(data, variables) {
      const props = variables as IDeleteInspectionInvitationProps
      queryClient.setQueryData(
        ['list_inspection_invitations', props.inspection_id],
        (oldData: unknown) => {
          if (!oldData) return [data]

          const oldInvitations = [...(oldData as IInspectionInvitation[])]

          const foundIndex = oldInvitations.findIndex((i) => i._id === props.invitation_id)

          if (foundIndex < 0) return oldInvitations

          oldInvitations.splice(foundIndex, 1)

          return oldInvitations
        },
      )
    },
  })
}

export const useSearchAdditionalCompany = (searchTerm: string) => {
  return useQuery({
    queryKey: ['search_additional_company', searchTerm],
    queryFn: () => searchAdditionalCompany(searchTerm),
    staleTime: 5 * 60 * 1000,
    enabled: searchTerm.length >= 3,
  })
}

export const useDashboardInspection = (inspectionId: string) => {
  return useQuery<IDashboardInspectionInformation>({
    queryKey: ['dashboard_inspection', inspectionId],
    queryFn: () => getDashboardInspectionInformation(inspectionId),
    staleTime: 5 * 60 * 1000,
  })
}

interface IUseInspectionCheckStatusToCloseProps {
  inspection_id: string
  shouldFetch?: boolean
}

export interface IUseInspectionCheckStatusToClose {
  ask_to_change_to_close: boolean
  is_there_open_findings: boolean
  message: string
}

export const useInspectionCheckStatusToClose = (props: IUseInspectionCheckStatusToCloseProps) => {
  return useQuery<IUseInspectionCheckStatusToClose>({
    queryKey: ['check_update_status_to_close', props.inspection_id],
    queryFn: () => getInspectionCheckStatusToClose(props.inspection_id),
    enabled: props.shouldFetch,
    retry: 2,
  })
}

export const useGetDiscrepancyWithPhotos = (discrepancyId: string, enabled?: boolean) => {
  return useQuery<IDiscrepancy>({
    queryKey: ['get_discrepancy_with_photos', discrepancyId],
    queryFn: () => getDiscrepancyWithPhotos(discrepancyId),
    enabled: enabled,
  })
}
export interface IGetPreviousInspectionParams {
  inspectionId: string
  enabled: boolean
}

export const useGetPreviousInspection = (params: IGetPreviousInspectionParams) => {
  return useQuery<IInspection[]>({
    queryKey: ['get_previous_inspection', params],
    queryFn: () => getPreviousInspection(params),
    enabled: params.enabled,
    retry: 2,
  })
}

export interface IAuditMigrateData {
  source_inspection_id: string
  target_inspection_id: string
  total_records: number
  total_processed_records: number
  status: string
  created_at: Date
}

export const useGetProgressInspectionMigrateData = (
  params: IgetProgressInspectionMigrateDataParams,
) => {
  return useQuery<IAuditMigrateData>({
    queryKey: ['get_progress_inspection_migrate_data', params],
    queryFn: () => getProgressInspectionMigrateData(params),
    retry: 1,
  })
}
